import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {Buffer} from 'buffer';
import {
  DEFAULT_DATASET,
  setDefaultDataset,
} from '@apps-common/geocorn-sdk/api/datasets.js';
import {DialogTitle} from '@apps-common/ui-components';
import {I18nextProvider} from 'react-i18next';
import {Polygon as PolygonIcon} from '@apps-common/ui-icons';
import {EXTENSIONS as SUPPORTED_EXTENSIONS} from '@apps-common/importer';
import {addFeatureToCollection} from '@apps-common/geocorn-sdk/api/features.js';
import {array, bool, func, number, object, shape, string} from 'prop-types';
import {
  countVertexes,
  dedupePoints,
} from '@apps-common/geocorn-sdk/geom/ops.js';
import {
  createCandidates,
  isTooComplex,
  isValidGeometry,
} from '@apps-common/geocorn-sdk/geom/simplify.js';
import {createCollection} from '@apps-common/geocorn-sdk/api/collections.js';
import {getArea} from '@apps-common/geocorn-sdk/geom/cea.js';
import {
  setAuthToken,
  setGraneroTarget,
} from '@apps-common/geocorn-sdk/api/index.js';

import SelectStep from './SelectStep.jsx';
import SimplifyStep from './SimplifyStep/index.jsx';
import UploadStep from './UploadStep.jsx';
import defaultI18N from './i18n.js';
import {
  addFiles,
  addGroupedFiles,
  defaultUploaderState,
  removeGroupedFile,
  setActiveGroupIndex,
  setFeatures,
  setFiles,
  setGroupedFiles,
  uploaderReducer,
} from './reducer.js';
import {
  extractZip,
  getFileExtension,
  getUniqueProperties,
  transformFeature,
} from './util.js';
import {importFeatures} from './import.js';

const STEPS = {
  UPLOAD: 'upload', // Upload selection step.
  SELECT: 'select', // choose which features to add to the AOA collection.
  SIMPLIFY: 'simplify', // Simplify features that have been uploaded
  COLLECTION: 'collection', // Step to select or create a collection
};

const DEFAULT_LABEL_PROP = 'name';

const defaultFeatureProps = {
  title: '',
  description: '',
  id: '',
};

const MAX_BYTES = 800000; // ~0.8MB

/**
 * Uploader modal
 *
 * @param {object} collection - Optional. If provided features will be uploaded to this collection.
 * @param {func} onClose - Callback for when the dialog is closed.
 * @param {number} maxVertexes - Defaults to 1500, maxiumum allowable vertexes per feature.
 * @param {string} title - The modal's title, defaults to "Import files."
 * @param {string} dataset - Which target dataset for the collection, defaults to "my" optional when a collection is provided.
 * @param {object} i18n - An i18n Instance, if available.
 */
export const Uploader = ({
  collection,
  dataset = DEFAULT_DATASET,
  graneroConfig,
  i18n = null,
  inFiles = [],
  maxVertexes = 1500,
  onClose,
  onError,
  onSuccess,
  open,
  title = 'Import files',
}) => {
  setDefaultDataset(dataset);

  if (graneroConfig?.apiTarget) {
    setGraneroTarget(graneroConfig.apiTarget);
  }
  if (graneroConfig?.authToken) {
    setAuthToken(graneroConfig.authToken);
  }

  // step in the process
  const [activeStep, setActiveStep] = useState(STEPS.UPLOAD);

  const [state, dispatch] = useReducer(uploaderReducer, defaultUploaderState);

  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureProperties, setFeatureProperties] =
    useState(defaultFeatureProps);
  const [selectedCollection, setSelectedCollection] = useState();
  const [processing, setProcessing] = useState(false);
  const [processingError, setProcessingError] = useState();
  const [importing, setImporting] = useState(false);
  const [createCollectionError, setCreateCollectionError] = useState();
  const [maxFeaturesWarning, setMaxFeaturesWarning] = useState();
  const [currentImportErrors, setCurrentImportErrors] = useState();

  const importedFeatures = useRef();
  const importErrors = useRef();

  const activeGroup = useMemo(
    () => state.groupedFiles[state.groupKeys[state.activeGroupIndex]],
    [state.activeGroupIndex, state.groupKeys, state.groupedFiles],
  );

  useEffect(() => {
    setSelectedCollection(collection);
  }, [collection]);

  const resetState = () => {
    setActiveStep(STEPS.UPLOAD);
    setFeatureProperties(defaultFeatureProps);
    setMaxFeaturesWarning();
    setSelectedCollection();
    dispatch(setFiles([]));
    dispatch(setGroupedFiles({}));
    dispatch(setActiveGroupIndex(null));
  };

  const handleOnClose = () => {
    resetState();
    if (onClose) {
      onClose();
    }
  };

  const onAddFeatures = features => {
    dispatch(setFeatures(features));
  };

  const onFinish = () => {
    if (importedFeatures.current) {
      if (onSuccess) {
        onSuccess(importedFeatures.current);
      }
      importedFeatures.current = [];
      resetState();
    }

    if (importErrors.current?.length > 0) {
      if (onError) {
        onError(importErrors.current);
      }
      importErrors.current = [];
    }
  };

  const doAddFeaturesToCollection = async (target, features) => {
    const updatedFeatures = [...features].map((feature, idx) =>
      transformFeature(feature, featureProperties, `feature #${idx + 1}`),
    );

    const results = [];

    // Chunk features into chunks of ~0.8MB as we need the payload to be < 1MB
    const {chunkedFeatures} = updatedFeatures.reduce(
      (accumulator, feature) => {
        if (accumulator.chunkedFeatures[accumulator.index]) {
          accumulator.chunkedFeatures[accumulator.index];
        } else {
          accumulator.chunkedFeatures.push([]);
        }

        const bytes = Buffer.byteLength(
          JSON.stringify(accumulator.chunkedFeatures[accumulator.index]),
        );

        if (bytes <= MAX_BYTES) {
          accumulator.chunkedFeatures[accumulator.index].push(feature);
        } else {
          accumulator.index++;
          accumulator.chunkedFeatures.push([feature]);
        }

        return accumulator;
      },
      {
        chunkedFeatures: [],
        index: 0,
      },
    );

    // Batch requests to API
    for (const chunk of chunkedFeatures) {
      const result = await addFeatureToCollection(target.id, {
        type: 'FeatureCollection',
        features: chunk,
      })
        .then(res => ({
          features: res,
        }))
        .catch(error => {
          return {
            features: chunk,
            collectionId: target.id,
            error: error.response,
          };
        });

      results.push(result);
    }

    const successfulResults = results.filter(r => !r?.error);
    const failedResults = results.filter(r => r?.error);

    setImporting(false);

    if (successfulResults.length > 0) {
      const newFeatures = successfulResults.map(r => r.features).flat();
      if (!importedFeatures.current) {
        importedFeatures.current = {};
      }

      importedFeatures.current = {
        ...importedFeatures.current,
        [target.id]: importedFeatures.current[target.id]
          ? [...importedFeatures.current[target.id], ...newFeatures]
          : newFeatures,
      };
    }

    if (failedResults.length > 0) {
      const errors = failedResults.map(r => r.error.data).flat();
      setCurrentImportErrors(errors);
      importErrors.current = importErrors.current
        ? importErrors.current.push(errors)
        : failedResults;
    }

    if (
      state.groupKeys.length > 1 &&
      state.activeGroupIndex < state.groupKeys.length - 1
    ) {
      setFeatureProperties(defaultFeatureProps);
      setMaxFeaturesWarning();
      dispatch(setActiveGroupIndex(state.activeGroupIndex + 1));
    } else {
      onFinish();
    }
  };

  const onImport = features => {
    setImporting(true);

    if (selectedCollection.id === 'new' || !selectedCollection.id) {
      //create the new collection
      createCollection({
        title: selectedCollection.title,
        description: selectedCollection.description,
      })
        .then(response => {
          setSelectedCollection(response);
          return doAddFeaturesToCollection(response, features);
        })
        .catch(error => {
          setImporting(false);
          setCreateCollectionError({
            collectionName: selectedCollection.title,
            error,
          });
        });
    } else {
      //add features to existing collection
      return doAddFeaturesToCollection(selectedCollection, features);
    }
  };

  useEffect(() => {
    const propertyNames = getUniqueProperties(state.features);

    // select name as default label prop if it's in the properties.
    const nameProp =
      propertyNames.find(name => name.toLowerCase() === DEFAULT_LABEL_PROP) ||
      'pl_title';
    setFeatureProperties(props => ({...props, title: nameProp}));

    // select id as default id prop if it's in the properties.
    const idProp = propertyNames.find(name => name.toLowerCase() === 'id');
    setFeatureProperties(props => ({...props, id: idProp || ''}));

    setSelectedFeatures(state.features);
  }, [state.features]);

  const groupFiles = useCallback((files, parent) => {
    const group = {};
    files.forEach(file => {
      const fileName =
        file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      const extension = getFileExtension(file.name);

      // is zip
      if (extension === 'zip' || extension === 'kmz') {
        extractZip(file).then(files => {
          groupFiles(files, file.name);
        });
      } else if (SUPPORTED_EXTENSIONS.includes(extension)) {
        if (!group[fileName]) {
          group[fileName] = {
            label: file.name,
            files: [file],
            parent,
          };
        } else {
          group[fileName].files.push(file);
          group[fileName].label = fileName;
        }
      }
    });

    dispatch(addGroupedFiles(group));
  }, []);

  useEffect(() => {
    if (inFiles.length) {
      dispatch(addFiles(inFiles));
      groupFiles(inFiles);

      setProcessing(true);
      dispatch(setActiveGroupIndex(0));
    }
  }, [groupFiles, inFiles]);

  useEffect(() => {
    if (state.activeGroupIndex !== null) {
      importFeatures(activeGroup?.files)
        .then(data => {
          if (data.maxFeaturesWarning) {
            setMaxFeaturesWarning(data.maxFeaturesWarning);
          }
          const hasMany = data.features.length > 1;

          const features = data.features.map((f, index) => {
            const titleSuffix = hasMany ? ` - ${index + 1}` : '';

            // reduce precision and remove any duplicate points
            const cleanGeometry = dedupePoints(f.geometry);

            const isComplex = isTooComplex(cleanGeometry, maxVertexes);
            const isValid = isValidGeometry(cleanGeometry);

            const isClean = !isComplex && isValid;
            const feature = {
              ...f,
              geometry: cleanGeometry,
              properties: {
                ...f.properties,
                pl_title: `${activeGroup.label}${titleSuffix}`,
              },
              pl_metadata: {
                is_too_complex: isComplex,
                is_valid: isValid,
                is_clean: !isComplex && isValid,
                vertex_count: countVertexes(cleanGeometry),
                area: getArea(cleanGeometry),
              },
            };

            if (!isClean) {
              const candidates = createCandidates(cleanGeometry, {
                maxVertexCount: maxVertexes,
              });

              feature.pl_metadata.candidates = candidates;
            }
            return feature;
          });

          onAddFeatures(features);
          setProcessing(false);
          setActiveStep(STEPS.SELECT);
        })
        .catch(error => {
          setProcessing(false);
          setProcessingError({
            file: activeGroup?.label,
            message: error.message,
          });
          dispatch(setActiveGroupIndex(null));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeGroupIndex]);

  return (
    <I18nextProvider i18n={i18n || defaultI18N}>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleOnClose();
          }
        }}
        PaperProps={{
          'data-testid': 'upload-modal',
          sx: {
            padding: 0,
            minHeight: 600,
          },
        }}
        scroll="paper"
      >
        <DialogTitle
          title={
            activeGroup
              ? `Import ${activeGroup.label} ${
                  state.groupKeys.length > 1
                    ? `(${state.activeGroupIndex + 1}\/${
                        state.groupKeys.length
                      })`
                    : ''
                }`
              : title
          }
          onClose={handleOnClose}
          icon={<PolygonIcon />}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />
        <Box
          display="grid"
          gridTemplateAreas={`
          "stepper content content"
          "stepper content content"
        `}
          gridTemplateRows="auto minmax(0, 1fr) auto"
          gridTemplateColumns="auto 1fr auto"
          minHeight={600}
          maxHeight={600}
          flex={1}
        >
          <Box gridArea="content" display="flex" flexDirection="column">
            {processing && (
              <Stack
                spacing={3}
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <CircularProgress color="primary" size={64} />
                <Typography variant="h6">Processing...</Typography>
              </Stack>
            )}
            {activeStep === STEPS.UPLOAD && !processing && (
              <>
                {processingError && (
                  <Alert severity="error" sx={{margin: 1}}>
                    {`There was an error processing ${processingError.file}.`}
                    <br />
                    {`error: ${processingError.message}`}
                  </Alert>
                )}

                <UploadStep
                  groupedFiles={state.groupedFiles}
                  onAddFiles={files => {
                    setProcessingError(null);
                    dispatch(setActiveGroupIndex(null));
                    dispatch(addFiles(files));
                    groupFiles([...state.files, ...files]);
                  }}
                  onRemoveFile={groupKey => {
                    setProcessingError();
                    dispatch(removeGroupedFile(groupKey));
                    setFeatureProperties(defaultFeatureProps);
                  }}
                  onAddFeatures={() => {
                    setProcessingError();
                    setProcessing(true);
                    dispatch(setActiveGroupIndex(0));
                  }}
                />
              </>
            )}

            {activeStep === STEPS.SELECT && (
              <SelectStep
                features={state.features}
                onFinished={() => {
                  const simplifiedFeatures = selectedFeatures.map(feature => {
                    if (feature.pl_metadata.is_clean) {
                      return feature;
                    } else {
                      return {
                        ...feature,
                        geometry: feature.pl_metadata.candidates[0].geometry,
                      };
                    }
                  });

                  onImport(simplifiedFeatures);
                }}
                onVerify={() => {
                  setActiveStep(STEPS.SIMPLIFY);
                }}
                selectedFeatures={selectedFeatures}
                setSelectedFeatures={setSelectedFeatures}
                collection={selectedCollection}
                setCollection={setSelectedCollection}
                featureProperties={featureProperties}
                setFeatureProperties={setFeatureProperties}
                importing={importing}
                maxFeaturesWarning={maxFeaturesWarning}
              />
            )}
            {activeStep === STEPS.SIMPLIFY && (
              <SimplifyStep
                maxVertexes={maxVertexes}
                features={state.features.filter(f => !f.pl_metadata.is_clean)}
                onFinished={nextFeatures => {
                  const validFeatures = state.features.filter(
                    f => f.pl_metadata.is_clean,
                  );

                  onImport([...validFeatures, ...nextFeatures]);
                }}
                onBack={() => setActiveStep(STEPS.SELECT)}
                importing={importing}
              />
            )}
          </Box>
        </Box>
      </Dialog>
      <Snackbar
        open={!!createCollectionError}
        onClose={() => setCreateCollectionError()}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert severity="error" onClose={() => setCreateCollectionError()}>
          {`There was an error while creating collection ${createCollectionError?.collectionName}`}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!currentImportErrors}
        onClose={() => {
          setCurrentImportErrors();
        }}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert
          severity="error"
          onClose={() => {
            setCurrentImportErrors();
          }}
        >
          {`There was an error while importing features ${activeGroup ? `from ${activeGroup.label}` : ''}`}
          <br />
          <br />
          {JSON.stringify(currentImportErrors)}
        </Alert>
      </Snackbar>
    </I18nextProvider>
  );
};

Uploader.propTypes = {
  /**
   * Collection to use as default destination
   */
  collection: object,
  /**
   * Dataset to use as default.
   */
  dataset: string,

  i18n: object,
  /**
   * List of File objects to pre-load for import
   */
  inFiles: array,
  /**
   * Number of maximum vertices for validation
   */
  maxVertexes: number,
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: func,
  /**
   * Callback fired when finished processing files
   * Returns an array of any errors that occurred while importing
   */
  onError: func,
  /**
   * Callback fired when finished processing files.
   * Returns an object with collection ids and the feature ids added
   */
  onSuccess: func,
  /**
   * If `true`, the component is shown.
   */
  open: bool,
  /**
   * Default dialog title
   */
  title: string,

  /**
   * Configuration settings for interacting with Granero
   */
  graneroConfig: shape({
    apiTarget: string,
    authToken: string,
  }),
};

export default Uploader;
